import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNewOutlined';
import PersonIcon from '@mui/icons-material/PersonOutlineOutlined';
import { useFilteredRows } from '../hooks/useFilteredRows';
import { useData } from '../store/data';
import { useFilters } from '../store/filters';
import { fmtInt, formatDate } from '../lib/format';
import { isDone } from '../lib/status';
import { inventoryFixVersions, issueHasFixVersion, type FixVersionInfo } from '../lib/fix-versions';
import { cpsTicketId } from '../lib/customer-bugs';
import { resolvedOf } from '../lib/jira-dates';
import { countBy, sortedByCount } from '../lib/filter-rows';
import { StatusChip } from './StatusChip';
import type { Issue } from '../types/issue';

type StatusScope = 'closed' | 'open' | 'all';
type TeckScope   = 'all' | 'only' | 'exclude';

// Prüft, ob ein Ticket ein Label enthält, das „teckentrup" (case-insensitive)
// im Namen hat. Erfasst alle Varianten: „teckentrup", „Stopper_teckentrup",
// „Teckentrup_Tests" …
function hasTeckentrupLabel(row: Issue): boolean {
  const labels = (row as Record<string, unknown>)['Labels'];
  if (!labels) return false;
  const vals = Array.isArray(labels) ? labels : [labels];
  for (const v of vals) {
    if (v && String(v).toLowerCase().includes('teckentrup')) return true;
  }
  return false;
}

const JIRA_URL_KEY = 'v2.jira.baseUrl';
function loadJiraBaseUrl(): string {
  if (typeof window === 'undefined') return 'https://schueco.atlassian.net';
  return (localStorage.getItem(JIRA_URL_KEY) || 'https://schueco.atlassian.net').replace(/\/+$/, '');
}
function jiraUrl(base: string, issueKey: string): string {
  return `${base}/browse/${encodeURIComponent(issueKey)}`;
}

export function ReleaseTickets() {
  const rows = useFilteredRows() as Issue[];
  const allRows = useData(s => s.rows) as Issue[];
  const teamFilter    = useFilters(s => s.team);
  const setFilters    = useFilters(s => s.set);
  const [selected, setSelected] = useState<FixVersionInfo | null>(null);
  const [scope, setScope] = useState<StatusScope>('closed');
  const [teck, setTeck]   = useState<TeckScope>('all');
  const jiraBase = useMemo(() => loadJiraBaseUrl(), []);

  // Alle Teams aus dem ungefilterten Snapshot — für die Chip-Reihe.
  const allTeams = useMemo(
    () => sortedByCount(countBy(allRows, 'Team Name')),
    [allRows]
  );

  const versions = useMemo(() => inventoryFixVersions(rows), [rows]);

  // Default-Auswahl: die erste (neueste) Fix-Version, sobald verfügbar
  const effectiveSelected = selected ?? versions[0] ?? null;

  const filteredTickets = useMemo(() => {
    if (!effectiveSelected) return [];
    const name = effectiveSelected.name;
    const out: Issue[] = [];
    for (const r of rows) {
      const t = r['Issue Type'];
      if (t !== 'Story' && t !== 'Bug') continue;
      if (!issueHasFixVersion(r, name)) continue;
      const done = isDone(String(r['Status'] ?? ''));
      if (scope === 'closed' && !done) continue;
      if (scope === 'open'   &&  done) continue;
      if (teck !== 'all') {
        const isTeck = hasTeckentrupLabel(r);
        if (teck === 'only'    && !isTeck) continue;
        if (teck === 'exclude' &&  isTeck) continue;
      }
      out.push(r);
    }
    // Sortierung: Resolved DESC, dann Key ASC
    out.sort((a, b) => {
      const ra = resolvedOf(a);
      const rb = resolvedOf(b);
      const ta = ra ? ra.getTime() : 0;
      const tb = rb ? rb.getTime() : 0;
      if (ta !== tb) return tb - ta;
      return String(a['Issue key'] ?? '').localeCompare(String(b['Issue key'] ?? ''));
    });
    return out;
  }, [rows, effectiveSelected, scope, teck]);

  // KPIs über die aktuelle Auswahl
  const kpi = useMemo(() => {
    let stories = 0, bugs = 0, cust = 0, intern = 0, opn = 0, done = 0;
    const teams = new Map<string, number>();
    for (const r of filteredTickets) {
      if (r['Issue Type'] === 'Story') stories++; else bugs++;
      if (r['Issue Type'] === 'Bug' && cpsTicketId(r) != null) cust++;
      else if (r['Issue Type'] === 'Bug') intern++;
      if (isDone(String(r['Status'] ?? ''))) done++; else opn++;
      const team = (r['Team Name'] ?? '(kein Team)') as string;
      teams.set(team, (teams.get(team) ?? 0) + 1);
    }
    return { stories, bugs, cust, intern, opn, done, teams };
  }, [filteredTickets]);

  if (versions.length === 0) {
    return (
      <Alert severity="info" sx={{ borderRadius: 3 }}>
        Keine Fix-Versionen in der aktuellen Auswahl. Filter oben lockern oder anderes Team wählen.
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      {/* Team-Filter-Chips — wirken global (auch auf andere Sections) */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          flexWrap: 'wrap',
          p: 1,
          bgcolor: 'action.hover',
          borderRadius: 2,
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 700, mr: 0.5, color: 'text.secondary' }}>
          TEAM
        </Typography>
        <Chip
          size="small"
          label="Alle"
          color={teamFilter === '' ? 'primary' : 'default'}
          variant={teamFilter === '' ? 'filled' : 'outlined'}
          onClick={() => setFilters({ team: '' })}
          clickable
        />
        {allTeams.map(([name, count]) => (
          <Chip
            key={name}
            size="small"
            label={`${name} (${count})`}
            color={teamFilter === name ? 'primary' : 'default'}
            variant={teamFilter === name ? 'filled' : 'outlined'}
            onClick={() => setFilters({ team: name })}
            clickable
          />
        ))}
      </Box>

      {/* Auswahl-Zeile */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} flexWrap="wrap">
        <Autocomplete
          size="small"
          options={versions}
          value={effectiveSelected}
          onChange={(_, v) => setSelected(v)}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(a, b) => a.name === b.name}
          groupBy={(o) => o.year ? `${o.year}` : '—'}
          renderOption={(props, o) => (
            <li {...props} key={o.name}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                <Typography sx={{ flex: 1 }}>{o.name}</Typography>
                <Chip size="small" label={`${fmtInt(o.count)} Tickets`} variant="outlined" sx={{ height: 20, fontSize: 11 }} />
              </Stack>
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} label="Fix Version / Service Pack" placeholder="Version wählen…" />
          )}
          sx={{ minWidth: 320, flex: 1 }}
        />
        <ToggleButtonGroup
          size="small"
          value={scope}
          exclusive
          onChange={(_, v) => v && setScope(v as StatusScope)}
        >
          <ToggleButton value="closed">nur closed</ToggleButton>
          <ToggleButton value="open">nur offen</ToggleButton>
          <ToggleButton value="all">alle</ToggleButton>
        </ToggleButtonGroup>

        <ToggleButtonGroup
          size="small"
          value={teck}
          exclusive
          onChange={(_, v) => v && setTeck(v as TeckScope)}
          title={'Filter nach Label „teckentrup" (case-insensitive, deckt „Stopper_teckentrup" und „Teckentrup_Tests" mit ab)'}
        >
          <ToggleButton value="all">Teckentrup: alle</ToggleButton>
          <ToggleButton value="only">nur Teckentrup</ToggleButton>
          <ToggleButton value="exclude">ohne Teckentrup</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {/* KPI-Zeile */}
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        <KpiChip label="Σ Tickets" value={filteredTickets.length} accent="primary" />
        <KpiChip label="Stories" value={kpi.stories} accent="#2e7d32" />
        <KpiChip label="Bugs" value={kpi.bugs} accent="#c62828" />
        <KpiChip label="Kunden-Bugs" value={kpi.cust} accent="#c62828" />
        <KpiChip label="interne Bugs" value={kpi.intern} accent="#ef6c00" />
        <KpiChip label="offen" value={kpi.opn} accent="text.secondary" />
        <KpiChip label="done" value={kpi.done} accent="#2e7d32" />
      </Stack>

      {/* Team-Aufteilung */}
      {kpi.teams.size > 1 && (
        <Card variant="outlined">
          <CardContent sx={{ py: 1.5 }}>
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Aufteilung nach Team (in dieser Selection)
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {Array.from(kpi.teams.entries())
                .sort((a, b) => b[1] - a[1])
                .map(([team, n]) => (
                  <Chip key={team} size="small" label={`${team}: ${fmtInt(n)}`} variant="outlined" />
                ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Detail-Tabelle */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Tickets in <b>{effectiveSelected?.name ?? '–'}</b>
            {scope === 'closed' && ' (nur geschlossene)'}
            {scope === 'open'   && ' (nur offene)'}
          </Typography>
          {filteredTickets.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              Keine Tickets in dieser Auswahl (Team-Filter oben oder Scope anpassen).
            </Typography>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: 560 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Key</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Typ</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Team</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Priority</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Resolved</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>CPS</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Summary</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTickets.slice(0, 500).map(r => {
                      const key  = String(r['Issue key'] ?? '');
                      const cps  = cpsTicketId(r);
                      const rDate = resolvedOf(r);
                      return (
                        <TableRow key={key} hover>
                          <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                            <Link href={jiraUrl(jiraBase, key)} target="_blank" rel="noopener noreferrer" underline="hover"
                              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}>
                              {key}
                              <OpenInNewIcon sx={{ fontSize: 12, opacity: 0.6 }} />
                            </Link>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={String(r['Issue Type'] ?? '')}
                              color={r['Issue Type'] === 'Story' ? 'success' : 'error'}
                              variant="outlined"
                              sx={{ height: 20, fontSize: 10.5 }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{String(r['Team Name'] ?? '–')}</TableCell>
                          <TableCell>
                            <StatusChip status={String(r['Status'] ?? '')} />
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                            {String(r['Priority'] ?? '–')}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                            {rDate ? formatDate(rDate) : '–'}
                          </TableCell>
                          <TableCell>
                            {cps ? (
                              <Chip
                                size="small"
                                icon={<PersonIcon fontSize="small" />}
                                label={cps}
                                color="primary"
                                clickable
                                component="a"
                                href={jiraUrl(jiraBase, cps)}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ height: 22, fontFamily: 'monospace' }}
                              />
                            ) : '–'}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, maxWidth: 480 }}>
                            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={String(r['Summary'] ?? '')}>
                              {String(r['Summary'] ?? '')}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {filteredTickets.length > 500 && (
                <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
                  Anzeige auf 500 Zeilen begrenzt (Σ {fmtInt(filteredTickets.length)}). Team-Filter oben nutzen zum Verkleinern.
                </Typography>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}

function KpiChip({ label, value, accent }: { label: string; value: number; accent: string }) {
  return (
    <Box
      sx={{
        px: 1.5,
        py: 0.75,
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        minWidth: 100,
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
        {label}
      </Typography>
      <Typography variant="h6" sx={{ color: accent === 'primary' ? 'primary.main' : accent, fontWeight: 700, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
        {fmtInt(value)}
      </Typography>
    </Box>
  );
}
